import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, HostListener, ChangeDetectorRef, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent } from '../../../shared/components/primeng';
import { AuthApiService } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';
import { ToasterService } from '../../../services/toaster.service';
import { ExportLoaderService } from '../../../services/export-loader.service';
import { ActionDropdownOverlayController } from '../../../shared/utils/action-dropdown-overlay.sync';

export interface ComputeRequestData {
  id: string;
  userName: string;
  organisationName: string;
  jobTitle: string;
  currentRole: string;
  requestedAt: string;
  status: string;
  action?: string;
  originalData?: any;
}

@Component({
  selector: 'app-user-compute-requests',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    ModalComponent,
  ],
  templateUrl: './user-compute-requests.component.html',
  styleUrl: './user-compute-requests.component.scss',
})
export class UserComputeRequestsComponent implements OnInit, AfterViewInit {
  @Input() showExportButton = true;

  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;

  columns: TableColumn[] = [
    {
      key: 'userName',
      label: 'UserName',
      sortable: true,
      width: '200px',
    },
    {
      key: 'organisationName',
      label: 'Organisation name',
      sortable: false,
      width: '220px',
    },
    {
      key: 'jobTitle',
      label: 'Job Title',
      sortable: false,
      width: '160px',
    },
    {
      key: 'currentRole',
      label: 'Current Role',
      sortable: false,
      width: '140px',
    },
    {
      key: 'requestedAt',
      label: 'Requested At',
      sortable: false,
      width: '160px',
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: '120px',
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '120px',
      align: 'left',
      fixed: 'right',
    },
  ];

  computeRequestsData: ComputeRequestData[] = [];
  loading = false;
  emptyMessage = 'No compute requests found';
  openDropdownId: string | null = null;

  /** Current sort state for server-side sorting */
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  /** View Details modal */
  isViewDetailsModalVisible = false;
  selectedRequest: ComputeRequestData | null = null;
  userDetails: any = null;
  userDetailsLoading = false;

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };

  constructor(
    private authApiService: AuthApiService,
    private formatService: FormatService,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {}
  private readonly exportLoader = inject(ExportLoaderService);

  ngOnInit(): void {
    this.loadComputeRequests();
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    if (this.actionCellTemplate) {
      const actionColumn = this.columns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.actionCellTemplate;
      }
    }
    
    if (this.statusCellTemplate) {
      const statusColumn = this.columns.find(col => col.key === 'status');
      if (statusColumn) {
        statusColumn.cellTemplate = this.statusCellTemplate;
      }
    }
  }

  async loadComputeRequests(): Promise<void> {
    this.loading = true;
    // Always close any open dropdown when the list refreshes
    this.openDropdownId = null;
    this.dropdownPosition = null;
    try {
      const sortString = this.getSortString();
      const response = await this.authApiService.getComputeRequest(
        this.pagination.currentPage,
        this.pagination.pageSize,
        sortString,
      );
      
      // Map response to ComputeRequestData format
      let mappedData: ComputeRequestData[] = [];
      
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray.map((item: any) => {
          // Format status to PascalCase
          const status = this.formatService.toPascalCase(item.status || 'N/A');
          // Current role: highest role from roles array, or fallback to explicit fields
          const currentRole = this.getHighestRoleFromItem(item);
          const requestedAtRaw =
            item.requestedAt ||
            item.requested_at ||
            item.createdAt ||
            item.created_at ||
            item.updatedAt ||
            item.updated_at ||
            '';
          
          return {
            id: item.id || '',
            userName: item.userName || item.user_name || item.applicantName || item.applicant_name || 'N/A',
            organisationName: item.organisationName || item.organisation_name || item.organizationName || item.organization_name || item.orgName || item.org_name || 'N/A',
            jobTitle: item.jobTitle || item.job_title || item.position || item.role || 'N/A',
            currentRole,
            requestedAt: requestedAtRaw ? this.formatService.formatDate(requestedAtRaw) : 'N/A',
            status: status,
            action: 'Action',
            originalData: item // Store original API response data
          };
        });
        
        // Update pagination from paginationInfo
        if (response['paginationInfo'] && response['paginationInfo']['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['paginationInfo']['totalCount']
          };
        }
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result array
        if (response['paginationInfo']['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['paginationInfo']['totalCount']
          };
        }
        mappedData = []; // No data to map
      }
      
      this.computeRequestsData = mappedData;
    } catch (error) {
      console.error('Error loading compute requests:', error);
      this.computeRequestsData = [];
    } finally {
      this.loading = false;
    }
  }

  onPageChange(event: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: event
    };
    this.loadComputeRequests();
  }

  onPageSizeChange(event: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: event,
      currentPage: 1
    };
    this.loadComputeRequests();
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadComputeRequests();
  }

  getSortString(): string | undefined {
    if (!this.sortColumn) return undefined;
    const apiFieldMap: Record<string, string> = {
      userName: 'userName',
    };
    const apiField = apiFieldMap[this.sortColumn];
    if (!apiField) return undefined;
    return `${apiField}:${this.sortDirection}`;
  }

  toggleActionDropdown(event: Event, row: ComputeRequestData): void {
    event.stopPropagation();
    const rowId = row.id || '';
    if (this.openDropdownId === rowId) {
      this.closeActionDropdown();
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    this.openDropdownId = rowId;
    this.actionDropdownOverlay.open({
      anchor: btn,
      itemCount: 3,
      onPosition: (pos) => {
        this.dropdownPosition = { top: pos.top, left: pos.left, right: pos.right };
        this.cdr.markForCheck();
      },
      onAnchorDetached: () => this.closeActionDropdown(),
    });
    this.cdr.detectChanges();
  }

  closeActionDropdown(): void {
    this.actionDropdownOverlay.close();
    this.openDropdownId = null;
    this.dropdownPosition = null;
    this.cdr.detectChanges();
  }

  isDropdownOpen(row: ComputeRequestData): boolean {
    const rowId = row.id || '';
    return this.openDropdownId === rowId;
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): ComputeRequestData | null {
    if (this.openDropdownId == null) return null;
    return this.computeRequestsData.find((r) => (r?.id ?? '') === this.openDropdownId) ?? null;
  }

  /** Position for the fixed dropdown overlay. */
  dropdownPosition: { top: number; left: number | null; right: number | null } | null = null;
  private readonly actionDropdownOverlay = new ActionDropdownOverlayController();

  isRequestProcessed(row: ComputeRequestData): boolean {
    // Check if request status is "Granted", "Approved", or "Rejected" (case-insensitive)
    const status = (row.status || '').toLowerCase();
    return status === 'granted' || status === 'approved' || status === 'rejected';
  }

  async handleAction(action: string, row: ComputeRequestData): Promise<void> {
    this.closeActionDropdown();

    if (action === 'view') {
      this.selectedRequest = row;
      this.isViewDetailsModalVisible = true;
      this.userDetails = null;
      const userId = this.getUserIdFromRow(row);
      if (userId) {
        this.userDetailsLoading = true;
        try {
          const response = await this.authApiService.getUserInfoById(userId);
          this.userDetails = response?.['result'] ?? response;
        } catch (error) {
          console.error('Error fetching user details:', error);
          this.toasterService.error('Failed to load user details.');
        } finally {
          this.userDetailsLoading = false;
          this.cdr.detectChanges();
        }
      } else {
        this.toasterService.error('User ID not found for this request.');
      }
      return;
    }

    // Don't proceed if request is already processed
    if (this.isRequestProcessed(row) && (action === 'approve' || action === 'reject')) {
      return;
    }

    if (action === 'approve') {
      await this.approveComputeRequest(row);
    } else if (action === 'reject') {
      await this.rejectComputeRequest(row);
    }
  }

  closeViewDetailsModal(): void {
    this.isViewDetailsModalVisible = false;
    this.selectedRequest = null;
    this.userDetails = null;
  }

  /** Extract userId from row (originalData) for admin/user API. */
  private getUserIdFromRow(row: ComputeRequestData): string | null {
    const orig = row.originalData;
    if (!orig) return null;
    return (
      orig.userId ??
      orig.user_id ??
      orig.applicantId ??
      orig.applicant_id ??
      orig.user?.id ??
      orig.user?.userId ??
      orig.applicant?.id ??
      orig.applicant?.userId ??
      null
    );
  }

  async approveComputeRequest(row: ComputeRequestData): Promise<void> {
    if (!row.id) {
      console.error('Request ID is missing');
      return;
    }

    try {
      await this.authApiService.approveComputeRequest(row.id, 'granted');
      this.toasterService.success('Request has been approved successfully');
      // Reload the data to reflect the updated status
      await this.loadComputeRequests();
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Error approving compute request:', error);
      // Check if error has detail message
      if (error?.error?.detail) {
        this.toasterService.error(error.error.detail);
      } else {
        this.toasterService.error('Failed to approve request. Please try again.');
      }
    }
  }

  async rejectComputeRequest(row: ComputeRequestData): Promise<void> {
    if (!row.id) {
      console.error('Request ID is missing');
      return;
    }

    try {
      await this.authApiService.approveComputeRequest(row.id, 'rejected');
      this.toasterService.success('Request has been rejected successfully');
      // Reload the data to reflect the updated status
      await this.loadComputeRequests();
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Error rejecting compute request:', error);
      // Check if error has detail message
      if (error?.error?.detail) {
        this.toasterService.error(error.error.detail);
      } else {
        this.toasterService.error('Failed to reject request. Please try again.');
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null &&
        !target.closest('.action-cell-wrapper') &&
        !target.closest('.action-dropdown-overlay')) {
      this.closeActionDropdown();
    }
  }

  /**
   * Get the highest role from the item's roles array, or fallback to explicit role fields.
   */
  private getHighestRoleFromItem(item: any): string {
    const roles = item?.roles;
    if (roles && Array.isArray(roles) && roles.length > 0) {
      const highest = this.formatService.getHighestRole(roles);
      if (highest) return highest;
      // formatService only includes admin/org-admin/provider/consumer; for others (e.g. compute), use first non-system role
      const systemRoles = ['default-roles-tgdex-pilot', 'offline_access', 'uma_authorization', 'default-roles-iudx-v2'];
      const meaningfulRole = roles.find((r: string) => r && !systemRoles.includes(String(r).toLowerCase()));
      if (meaningfulRole) return this.formatService.toPascalCase(meaningfulRole);
    }
    return item.currentRole || item.current_role || item.role || item.userRole || item.user_role || 'N/A';
  }

  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip status-chip-default';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'pending' || statusLower === 'pending approval') {
      return 'status-chip status-chip-pending';
    } else if (statusLower === 'granted' || statusLower === 'approved') {
      return 'status-chip status-chip-granted';
    } else if (statusLower === 'rejected') {
      return 'status-chip status-chip-rejected';
    }
    
    return 'status-chip status-chip-default';
  }

  async onExport(): Promise<void> {
    try {
      await this.exportLoader.run(async () => {
        const response = await this.authApiService.exportComputeRequestsReport();
        if (!response) return;
        const blob =
          response instanceof Blob
            ? response
            : new Blob([response as unknown as BlobPart], {
                type: 'application/octet-stream',
              });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'compute-requests-report.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error('Export failed:', error);
    }
  }
}
